import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useIdioma } from "@/context/IdiomaContext";
import { SelectorIdioma } from "@/components/SelectorIdioma";
import { ArrowLeft } from "lucide-react";
import { LogoBspark } from "@/components/LogoBspark";

/** Renderiza el markdown sencillo que se guarda en los documentos legales. */
const bloques = (texto = "") => {
  const salida = [];
  let lista = [];
  const cerrarLista = (clave) => {
    if (lista.length) {
      salida.push(
        <ul key={`ul-${clave}`} className="list-disc pl-6 space-y-1.5 text-muted-foreground">
          {lista.map((li, j) => <li key={j}>{negritas(li)}</li>)}
        </ul>
      );
      lista = [];
    }
  };
  texto.split("\n\n").forEach((bloque, i) => {
    const lineas = bloque.trim().split("\n");
    let parrafo = [];
    const cerrarParrafo = (clave) => {
      if (parrafo.length) {
        salida.push(
          <p key={`p-${clave}`} className="text-muted-foreground leading-relaxed">
            {negritas(parrafo.join(" "))}
          </p>
        );
        parrafo = [];
      }
    };
    lineas.forEach((linea, j) => {
      const l = linea.trim();
      const clave = `${i}-${j}`;
      if (l.startsWith("## ")) {
        cerrarLista(clave);
        cerrarParrafo(clave);
        salida.push(
          <h2 key={`h-${clave}`} className="font-display text-lg md:text-lg font-bold mt-9 mb-1">
            {l.slice(3)}
          </h2>
        );
      } else if (l.startsWith("- ")) {
        cerrarParrafo(clave);
        lista.push(l.replace(/^-\s*/, ""));
      } else if (l && lista.length) {
        lista[lista.length - 1] += ` ${l}`;
      } else if (l) {
        cerrarLista(clave);
        parrafo.push(l);
      }    });
    cerrarLista(`fin-${i}`);
    cerrarParrafo(`fin-${i}`);
  });
  return salida;
};

const negritas = (texto) =>
  texto.split(/\*\*(.+?)\*\*/g).map((parte, i) =>
    i % 2 ? <strong key={i} className="text-foreground">{parte}</strong> : parte);

export default function Legal() {
  const { slug } = useParams();
  const { idioma, t } = useIdioma();
  const [doc, setDoc] = useState(null);
  const [lista, setLista] = useState([]);

  useEffect(() => {
    axios.get(`${API}/publico/legal/${slug}`, { params: { idioma } })
      .then(({ data }) => setDoc(data)).catch(() => setDoc(null));
    axios.get(`${API}/publico/legal`, { params: { idioma } }).then(({ data }) => setLista(data));
  }, [slug, idioma]);

  return (
    <div className="min-h-screen bg-background" data-testid="legal-page">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <LogoBspark className="h-14 w-14" texto={false} />
            <span className="font-display font-bold text-lg">FamilyHub</span>
          </Link>
          <SelectorIdioma />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14">
        <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
          BSpark Family Hub
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight"
          data-testid="legal-titulo">
          {doc?.titulo || ""}
        </h1>
        {doc && (
          <p className="text-sm text-muted-foreground mt-3">
            {t("legal.version")} {doc.version}
            {doc.actualizado ? ` · ${t("legal.actualizado")}: ${doc.actualizado.slice(0, 10)}` : ""}
          </p>
        )}
        <div className="mt-10 space-y-4" data-testid="legal-contenido">{bloques(doc?.contenido)}</div>
        {idioma === "en" && (
          <p className="text-xs text-muted-foreground mt-10">{t("legal.aviso")}</p>
        )}

        <nav className="flex flex-wrap gap-x-6 gap-y-2 mt-14 pt-8 border-t border-border text-sm">
          {lista.filter((l) => l.slug !== slug).map((l) => (
            <Link key={l.slug} to={`/legal/${l.slug}`} className="text-primary hover:opacity-80"
              data-testid={`legal-link-${l.slug}`}>
              {l.titulo}
            </Link>
          ))}
          <Link to="/" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
            <ArrowLeft className="h-3.5 w-3.5" /> {t("legal.volver")}
          </Link>
        </nav>
      </main>
    </div>
  );
}
